import { Chip, Icons, Select } from '@dbrain/ui';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../experience.js';

/**
 * What the library pages share (Skill'ler, Bağlantılar, Belgeler; SkillKutuphanesi, BaglantilarYeni,
 * BelgelerYeni): one vocabulary for where a thing lives (Ekip, Herkes, Kişisel, Çalışma alanları, then
 * Standart or Sohbet ekleri), scope chips with their counts, and sections in that order, each full width on
 * the page's one grid; with "Tümü" an empty section is not shown (D-118).
 */
export type LibraryScope = 'team' | 'organization' | 'user' | 'workspace' | 'builtin' | 'conversation';

/** the order of the sections on every library page */
export const SCOPE_ORDER: readonly LibraryScope[] = ['team', 'organization', 'user', 'workspace', 'builtin', 'conversation'];

export function scopeRank(scope: LibraryScope): number {
  return SCOPE_ORDER.indexOf(scope);
}

/** "Tümü", then the scopes with their counts; the page decides which and in what order */
export function ScopeChips<K extends string>({
  items,
  active,
  onChange,
  label,
  testId,
}: {
  items: { key: K; label: string; count?: number }[];
  active: K;
  onChange: (key: K) => void;
  label: string;
  testId: string;
}) {
  // Phase 16a: a person's own account has one place (Kişisel); there is nothing to choose between
  const capabilities = useCapabilities();
  if (!capabilities.organization) return null;
  return (
    <div className="ui-tabs ui-tabs--outlined scope-chips" role="group" aria-label={label}>
      {items.map((item) => (
        <Chip key={item.key} active={active === item.key} className="ui-tab" onClick={() => onChange(item.key)} data-testid={`${testId}-${item.key}`}>
          {item.label}
          {item.count !== undefined ? <span className="scope-chips__count">{item.count}</span> : null}
        </Chip>
      ))}
    </div>
  );
}

/**
 * One section: its title, a line on what is in it and how many. `collapsed` makes it a closed <details> with
 * one note at its top (another team's skills, seen for management).
 */
export function LibrarySection({
  title,
  hint,
  count,
  children,
  collapsed,
  testId,
}: {
  title: ReactNode;
  hint?: ReactNode;
  count: ReactNode;
  children: ReactNode;
  collapsed?: { note: ReactNode };
  testId?: string;
}) {
  const id = useId();
  const head = (
    <>
      <h2 id={id} className="library-section__title">
        {title}
      </h2>
      {hint ? <span className="library-section__hint">{hint}</span> : null}
      <span className="library-section__count">· {count}</span>
    </>
  );
  if (collapsed) {
    return (
      <details className="library-section library-section--collapsed" data-testid={testId}>
        <summary className="library-section__head">
          <Icons.chevronRight size={16} className="library-section__chevron" />
          {head}
        </summary>
        <p className="library-section__note">{collapsed.note}</p>
        {children}
      </details>
    );
  }
  return (
    <section className="library-section" aria-labelledby={id} data-testid={testId}>
      <header className="library-section__head">{head}</header>
      {children}
    </section>
  );
}

/** Where a thing lives, as a small coloured tag: "Ekip: Hukuk", "Herkes", "Kişisel", "Alan: Satış", "Standart" */
export function ScopeTag({ scope, name }: { scope: LibraryScope; name?: string | null }) {
  const { t } = useTranslation();
  const text =
    scope === 'team' && name ? t('library.tags.teamNamed', { name }) : scope === 'workspace' && name ? t('library.tags.workspaceNamed', { name }) : t(`library.tags.${scope}`);
  return (
    <span className={`scope-tag scope-tag--${scope}`} title={text} data-testid="scope-tag" data-scope={scope}>
      {text}
    </span>
  );
}

/** "Varsayılan açık" or "Zorunlu" for what the organization or a team turns on for its members */
export function ModeTag({ mode }: { mode: 'available' | 'default_on' | 'required' | null }) {
  const { t } = useTranslation();
  if (mode !== 'default_on' && mode !== 'required') return null;
  return (
    <span className={`scope-tag mode-tag mode-tag--${mode}`} data-testid="mode-tag" data-mode={mode}>
      {t(`library.modes.${mode}`)}
    </span>
  );
}

/** An ⓘ that shows a short explanation on hover or focus; screen readers read it as the button's description. */
export function InfoTip({ label, text, testId }: { label: string; text: string; testId?: string }) {
  const id = useId();
  return (
    <button type="button" className="info-tip" aria-label={label} aria-describedby={id} data-testid={testId}>
      <Icons.info size={14} />
      <span id={id} role="tooltip" className="info-tip__text">
        {text}
      </span>
    </button>
  );
}

/** "Anahtarlar şurada geçerli: Her yerde ▾": where the switches on the page apply (everywhere or one workspace) */
export function PlaceSelect({ value, onChange, workspaces, hint, testId }: { value: string; onChange: (id: string) => void; workspaces: { id: string; name: string }[]; hint: string; testId: string }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <span className="place-select" title={hint}>
      <label htmlFor={id} className="place-select__label">
        {t('library.appliesIn')}
      </label>
      <span className="chip-select">
        <Select id={id} size="sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
          <option value="">{t('library.everywhere')}</option>
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </Select>
      </span>
    </span>
  );
}
